import React, { useCallback } from 'react';
import { memo } from 'react';

export interface GameEntityCardProps {
    id: string;
    title: string;
    iconPath: string;
    isSelected?: boolean;
    onClick: (id: string) => void;
    description?: string;
    className?: string;
    imageSize?: 'sm' | 'md' | 'lg';
    imageStyle?: React.CSSProperties;
    children?: React.ReactNode;
}

/**
 * A shared component for displaying a selectable game entity (Knight, etc.)
 * Standardizes the look of selection states, image borders, and layout.
 */
export const GameEntityCard: React.FC<GameEntityCardProps> = memo(({
    id,
    title,
    iconPath,
    isSelected = false,
    onClick,
    description,
    className = "",
    imageSize = 'md',
    imageStyle,
    children
}) => {
    const handleClick = useCallback(() => {
        onClick(id);
    }, [onClick, id]);

    const imgDims = {
        sm: 'w-16 h-16',
        md: 'w-20 h-20',
        lg: 'w-24 h-24'
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className={`
                flex flex-col items-center rounded-lg transition-all duration-300 border-2
                ${isSelected 
                    ? 'bg-sworn-dark-brown border-sworn-gold shadow-lg ring-1 ring-sworn-gold/20' 
                    : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800 hover:border-slate-500'
                }
                ${className}
            `}
            aria-label={`${title}${isSelected ? ' selected' : ''}`}
        >
            <div className="relative p-2 pb-0">
                <img 
                    src={iconPath} 
                    alt={title} 
                    className={`
                        ${imgDims[imageSize]} 
                        object-cover rounded-md border-2 
                        ${isSelected ? 'border-sworn-gold' : 'border-slate-600'}
                        shadow-sm
                    `}
                    style={imageStyle}
                />
                {children}
            </div>
            
            <div className="p-3 text-center w-full">
                <h4 className={`font-semibold ${isSelected ? 'text-sworn-gold' : 'text-slate-200'}`}>
                    {title}
                </h4>
                
                {description && (
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2 px-1">
                        {description}
                    </p>
                )}
            </div>
        </button>
    );
});
